import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { listScratchCampaigns } from '@/lib/api/scratch-campaigns.service'
import {
  isScratchCampaignVisibleInWinnersLookup,
  scratchCampaignToSelectOption,
} from '@/lib/institution-scratch-campaign-select'
import type { RaspadinhaCampaign } from '@/types/raspadinha-campaign'

interface UseInstitutionScratchCampaignSelectOptions {
  enabled?: boolean
}

export function useInstitutionScratchCampaignSelect({
  enabled = true,
}: UseInstitutionScratchCampaignSelectOptions = {}) {
  const { guard } = useAuth()
  const { institutionId } = useInstitution()

  const isAdminWithoutInstitution = guard === 'admin' && institutionId === null

  const [campaigns, setCampaigns] = useState<RaspadinhaCampaign[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(true)
  const [campaignsError, setCampaignsError] = useState<string | null>(null)
  const [campaignValue, setCampaignValue] = useState('')

  const loadCampaigns = useCallback(async () => {
    if (!enabled) {
      setCampaigns([])
      setCampaignsLoading(false)
      setCampaignsError(null)
      return
    }

    setCampaignsLoading(true)
    setCampaignsError(null)

    if (isAdminWithoutInstitution) {
      setCampaigns([])
      setCampaignsLoading(false)
      return
    }

    try {
      const result = await listScratchCampaigns()
      setCampaigns(result)
    } catch {
      setCampaigns([])
      setCampaignsError('Não foi possível carregar as campanhas raspadinha.')
    } finally {
      setCampaignsLoading(false)
    }
  }, [enabled, isAdminWithoutInstitution])

  useEffect(() => {
    void loadCampaigns()
  }, [loadCampaigns])

  const campaignOptions = useMemo(
    () =>
      campaigns
        .filter(isScratchCampaignVisibleInWinnersLookup)
        .map(scratchCampaignToSelectOption),
    [campaigns]
  )

  useEffect(() => {
    if (campaignsLoading) {
      return
    }

    if (campaignOptions.length === 0) {
      setCampaignValue('')
      return
    }

    setCampaignValue((prev) => {
      if (prev && campaignOptions.some((option) => option.value === prev)) {
        return prev
      }
      return campaignOptions[0].value
    })
  }, [campaignsLoading, campaignOptions])

  const handleCampaignValueChange = useCallback((value: string) => {
    setCampaignValue(value)
  }, [])

  const selectPlaceholder = isAdminWithoutInstitution
    ? 'Selecione uma instituição acima'
    : campaignsLoading
      ? 'Carregando campanhas raspadinha...'
      : campaignOptions.length === 0
        ? 'Nenhuma campanha raspadinha cadastrada'
        : 'Selecione uma campanha raspadinha'

  const isSelectorDisabled =
    campaignsLoading || campaignOptions.length === 0 || isAdminWithoutInstitution

  const scratchCampaignId = useMemo((): number | null => {
    if (!campaignValue) {
      return null
    }
    const parsed = Number.parseInt(campaignValue, 10)
    return Number.isFinite(parsed) ? parsed : null
  }, [campaignValue])

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => String(campaign.id) === campaignValue) ?? null,
    [campaigns, campaignValue]
  )

  return {
    campaigns,
    campaignOptions,
    campaignValue,
    handleCampaignValueChange,
    selectPlaceholder,
    campaignsLoading,
    campaignsError,
    isSelectorDisabled,
    isAdminWithoutInstitution,
    scratchCampaignId,
    selectedCampaign,
  }
}
